import { faFileAlt } from '@fortawesome/pro-light-svg-icons'

import { DashboardModule, Link } from '@domain-group/suit-theme'

import CampaignQuotes from '@components/CustomizableDashboardModules/CampaignQuotes/CampaignQuotesContainer'
import EnabledPermission from '@components/EnabledPermission'

import useCampaignQuotes from '@hooks/useCampaignQuotes'
import { generateUrls } from '@hooks/useCampaignQuote'
import useSelector from '@hooks/useSelector'

import type { CampaignQuoteRequestOptions, UserModel } from '@models/types'

import { getCampaignType } from '@functions/campaign'

export const icon = faFileAlt

type ConfigOptionsParams = {
  currentUser?: UserModel,
  isAgent?: boolean,
  isCampaignManager?: boolean,
}

export const configOptions = (params: ConfigOptionsParams) => {
  const { currentUser, isAgent, isCampaignManager } = params

  const options = {
    title: {
      component: 'input',
      key: 'title',
      label: 'Title',
      optionKey: 'customization',
    },
    status: {
      component: 'select',
      key: 'status_key',
      label: 'Status',
      optionKey: 'queryParams',
      values: [
        { key: 'all', label: 'All' },
        { key: 'pending', label: 'Pending' },
        { key: 'approved', label: 'Approved' },
      ],
    },
    authorized: {
      component: 'select',
      key: 'authorized',
      label: 'Authorized',
      optionKey: 'queryParams',
      values: [
        { key: 'all', label: 'All' },
        { key: 'true', label: 'Yes' },
        { key: 'false', label: 'No' },
      ],
    },
    authorization_sent: {
      component: 'select',
      key: 'authorization_sent',
      label: 'Authorization Sent',
      optionKey: 'queryParams',
      values: [
        { key: 'all', label: 'All' },
        { key: 'true', label: 'Yes' },
        { key: 'false', label: 'No' },
      ],
    },
    priority_status: {
      component: 'select',
      hidden: isCampaignManager || isAgent,
      key: 'priority_quotes_by_user_id',
      label: 'Priority Status',
      optionKey: 'queryParams',
      values: [
        { key: 'all', label: 'All' },
        { key: currentUser?.id, label: 'Priority Only' },
      ],
    },
    display_mode: {
      component: 'select',
      key: 'display_mode',
      label: 'Display Mode',
      optionKey: 'customization',
      values: [
        { key: 'compact-list', label: 'List (Compact)' },
        { key: 'list', label: 'List (Large)' },
      ],
    },
    limit: {
      component: 'select',
      key: 'limit',
      label: 'Limit',
      optionKey: 'queryParams',
      values: [
        { key: 5, label: '5' },
        { key: 10, label: '10' },
        { key: 25, label: '25' },
        { key: 50, label: '50' },
      ],
    },
  }

  const filteredOptions = Object.fromEntries(
    // @ts-expect-error TS2339
    Object.entries(options).filter(([key, value]) => !value.hidden),
  )

  return filteredOptions
}

interface QuotesProps {
  customOptions: {
    moduleProps: {
      isCampaignManager: boolean,
    },
    queryParams: {
      authorization_sent?: boolean,
      authorized?: boolean,
      campaign_id?: number,
      limit?: number,
      status?: string,
      string?: string,
    },
    customization: {
      display_mode?: string,
      title?: string,
    },
    requestOptions?: CampaignQuoteRequestOptions,
  },
  editing: boolean,
}

const Quotes = (props: QuotesProps) => {
  const {
    customOptions,
    customOptions: {
      queryParams,
      requestOptions,
    },
    editing,
  } = props

  const { campaign_id: campaignId } = queryParams

  const { customization, moduleProps } = customOptions
  const { display_mode, title } = customization || {}
  const { isCampaignManager } = moduleProps || {}

  const { loading } = useCampaignQuotes()

  const { campaignQuotesIndexUrl, campaignQuotesOverviewUrl } = generateUrls({}, {
    prefixRoute: `campaigns/${campaignId}/edit`,
  })

  const quotesUrl = isCampaignManager ? campaignQuotesIndexUrl : campaignQuotesOverviewUrl

  const campaigns = useSelector(reduxState => reduxState.entities.campaigns)
  const campaign = campaignId && campaigns[campaignId] ? campaigns[campaignId] : {}

  return (
    <DashboardModule
      editing={editing}
      headerRight={(
        <EnabledPermission
          campaignType={getCampaignType(campaign)}
          permissionKey="campaign.quotes.manage"
        >
          <Link href={quotesUrl} textProps={{ fontSize: 'small' }}>
            Manage
          </Link>
        </EnabledPermission>
      )}
      loading={loading}
      title={title || 'Quotes'}
    >
      {!loading && (
        <CampaignQuotes
          displayMode={display_mode}
          // @ts-expect-error TS2322
          queryParams={queryParams}
          isCampaignManager={isCampaignManager}
          requestOptions={requestOptions}
        />
      )}
    </DashboardModule>
  )
}

export default Quotes
